import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { CERTIFICATE_MESSAGES } from '@/epics/certificateManagement/constants/translations/messages';

interface CertificateSpotlightProps {
  anchorEl: HTMLElement | null;
  onDismiss: () => void;
}

const LABEL = CERTIFICATE_MESSAGES.CERTIFICATE_SPOTLIGHT_LABEL.polish;
const AUTO_DISMISS_MS = 5000;

/**
 * One-time coach-mark overlay for the certificate nav icon.
 * Renders a full-screen dark overlay with a circular cutout centred on
 * anchorEl, a callout bubble to the right, and auto-dismisses after 5 s.
 */
export function CertificateSpotlight({
  anchorEl,
  onDismiss,
}: CertificateSpotlightProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [onDismiss]);

  if (!anchorEl) {
    return null;
  }

  const rect = anchorEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const r = rect.width / 2 + 10;

  return createPortal(
    <div
      className="fixed inset-0"
      style={{ zIndex: 200 }}
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
      aria-label={LABEL}
    >
      {/* Dark overlay with circular cutout */}
      <svg
        className="absolute inset-0"
        width="100%"
        height="100%"
        style={{ display: 'block', pointerEvents: 'none' }}
      >
        <defs>
          <mask id="cert-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <circle cx={cx} cy={cy} r={r} fill="black" />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#cert-spotlight-mask)"
        />
        {/* Subtle white ring around the cutout */}
        <circle
          cx={cx}
          cy={cy}
          r={r + 2}
          fill="none"
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="2"
        />
      </svg>

      {/* Callout bubble to the right of the spotlight */}
      <div
        style={{
          position: 'absolute',
          left: rect.right + 8,
          top: cy,
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        {/* Left-pointing arrow (tip toward icon) — 8×16 per Figma token spacing/small */}
        <svg width="8" height="16" viewBox="0 0 8 16" style={{ flexShrink: 0 }}>
          <path d="M0 8 L8 0 L8 16 Z" fill="#181A1B" />
        </svg>
        <div
          style={{
            background: '#181A1B',
            color: '#fff',
            borderRadius: 8,
            padding: 8,
            fontSize: 12,
            fontFamily: 'Roboto, system-ui, sans-serif',
            fontWeight: 400,
            lineHeight: '16px',
            letterSpacing: '0.4px',
            textAlign: 'center',
            maxWidth: 220,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}
        >
          {LABEL}
        </div>
      </div>
    </div>,
    document.body
  );
}
